import * as React from 'react'

import { ActionsHeader } from './headers/ActionsHeader'
import { Breadcrumbs } from '@user/components/DetailPages/Breadcrumbs'
import { ClosedDecorationHeader } from './headers/ClosedDecorationHeader'
import { DetailPageHeader } from '@user/components/DetailPages/Header'
import { ENTITY_TYPES } from '@sales-center/common'
import type { ILeadDetail } from '@user/store/lead/typings'
import { LEAD_STATUSES } from '../LeadCard/typings'
import { Notes } from '../../DetailPages/sections/Notes'
import { RequirementsToClose } from '@user/components/RequirementsToClose'
import { Contacts } from '@user/components/DetailPages/sections/Contacts'

import AppBar from '@mui/material/AppBar'
import { SchedulingWizardContextProvider } from '@user/components/Scheduling'
import {
  useData as useDataActivities,
  useFetchAllEntityActivities,
} from '@user/features/Activity/components/ActivitiesContent/hooks'
import {
  EntityDatesPanel,
  EntityDetailsAdditionalInformation,
  EntityDetailsContentWrapperStyled,
} from '@user/features/EntityDetails/components'
import { CompanyIdentifier } from '@user/features/CompanyIdentifier'
import { useData } from './useData'
import { useHasScheduledAppointment } from '@user/store/scheduling/hooks'
import { CWQDialogContextProvider } from '@user/components/SoldServices/CWQDialogContext'
import { ActivityTimelineCard } from '@user/components/DetailPages/sections/ActivityTimeline/ActivityTimelineCard/ActivityTimelineCard'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Hidden from '@mui/material/Hidden'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import LocationContextProvider from '@user/components/EntityCards/Locations/LocationsPopoverButton/context/LocationContextProvider'
import { Measure } from '@user/components/DetailPages/sections/EntityName/MeasureAI/Measure'
import useMediaQuery from '@mui/material/useMediaQuery'
import { usePropertyDetailsEnabled } from '@user/components/DetailPages/sections/EntityName/MeasureAI/hooks/usePropertyDetailsEnabled'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { getLeadDetail } from '@user/store/lead'
import { LeadCompanyRequiredFieldsProvider } from '@user/features/LeadCompanyRequiredFields/LeadCompanyRequiredFieldsContext'
import {
  getMostRecentKnockStatusFromLocations,
  MostRecentKnockStatus,
} from '@user/features/MapView/MostRecentKnockStatus'
import { LeadCompanyRequiredFieldsBanner } from '@user/features/LeadCompanyRequiredFields/LeadCompanyRequiredFieldsBanner'

interface Props {
  detail: ILeadDetail
  canConvertLeads: boolean
  canUpdateLeads: boolean
  canDeleteLeads: boolean
}

export function LeadDetailPageContent(props: Props) {
  const { detail, canConvertLeads, canUpdateLeads, canDeleteLeads } = props

  const {
    entityName,
    leadStatus,
    leadMatched,
    primaryContactMatched,

    addContactOpen,
    onOpenAddContact,
    onCloseAddContact,
    locations,
  } = useData({
    detail,
  })

  const hasScheduledAppointment =
    useHasScheduledAppointment({
      entityId: detail.id,
      entityType: ENTITY_TYPES.Lead,
      scheduleSettings: detail.scheduleSettings,
    }) ?? false

  const { Content } = useDataActivities()

  const isMobile = useMediaQuery('(max-width:900px)')
  const isSmallScreen = useMediaQuery('(max-width:1125px)')

  useFetchAllEntityActivities(detail.id, ENTITY_TYPES.Lead)
  const theme = useTheme()
  const measureAIEnabled = usePropertyDetailsEnabled()
  const recentKnockStatus = getMostRecentKnockStatusFromLocations(
    locations.serviceLocations,
  )

  const dispatch = useAppDispatch()
  const refreshDetail = React.useCallback(() => {
    dispatch(getLeadDetail(detail.id))
  }, [dispatch, detail.id])

  return (
    <LeadCompanyRequiredFieldsProvider lead={detail} onRefresh={refreshDetail}>
      <CWQDialogContextProvider>
        <SchedulingWizardContextProvider
          entityId={detail.id}
          entityType={ENTITY_TYPES.Lead}
          salesFunnelId={detail.salesFunnelId}
        >
          <LocationContextProvider
            entityId={detail.id}
            entityType={ENTITY_TYPES.Lead}
            locations={locations.serviceLocations || []}
          >
            <AppBar position="sticky" color="transparent" elevation={0}>
              <CompanyIdentifier variant="detail" />
              <Breadcrumbs
                entityType={ENTITY_TYPES.Lead}
                entityName={entityName}
              />
              <ClosedDecorationHeader detail={detail} status={leadStatus} />
              <Box boxShadow="0px 2px 4px rgba(202, 202, 202, 0.5)">
                <ActionsHeader
                  detail={detail}
                  canUpdate={canUpdateLeads}
                  canDelete={canDeleteLeads}
                  canConvert={canConvertLeads}
                />
              </Box>
            </AppBar>

            <LeadCompanyRequiredFieldsBanner />
            <Stack
              direction="row"
              alignItems="start"
              marginX="auto"
              maxWidth={1368}
              overflow="auto"
            >
              <EntityDetailsContentWrapperStyled>
                <Hidden lgUp>
                  <Stack m={2} mb={0}>
                    <EntityDatesPanel
                      detail={detail}
                      canUpdate={canUpdateLeads}
                      canDelete={canDeleteLeads}
                      canConvert={canConvertLeads}
                    />
                    <MostRecentKnockStatus
                      knockStatus={recentKnockStatus}
                      sx={{ mt: 1 }}
                    />
                  </Stack>
                </Hidden>
                <DetailPageHeader
                  color={theme.palette.common.white}
                  companyName={detail.company}
                  detail={detail}
                  disableEditName={
                    primaryContactMatched ||
                    !canUpdateLeads ||
                    leadStatus == LEAD_STATUSES.Disqualified
                  }
                  entityName={entityName}
                  entityStatus={leadStatus}
                  entityType={ENTITY_TYPES.Lead}
                  initialValue={detail.initialValue}
                  recurringValue={detail.recurringValue}
                  totalValue={detail.totalLeadValue}
                  canUpdate={canUpdateLeads}
                  hasScheduledAppointment={hasScheduledAppointment}
                  mt={isSmallScreen && recentKnockStatus ? 1 : 2}
                />
                {isMobile && measureAIEnabled && (
                  <Stack m={2} mt={0}>
                    <Measure
                      entityId={detail.id}
                      entityType={ENTITY_TYPES.Lead}
                      variant="location"
                    />
                  </Stack>
                )}

                <Stack m={2} mt={0}>
                  <Paper elevation={0} variant="outlined">
                    <Stack p={2}>
                      <Typography
                        color={theme.palette.text.primary}
                        mb={2.2}
                        fontSize="20px"
                        fontFamily="Source Sans Pro Semibold"
                      >
                        Closing Activities
                      </Typography>
                      <RequirementsToClose
                        services={detail.closingRequirements.appointments}
                        address={detail.serviceContact.street1}
                        branchId={detail.branchId}
                        canUpdate={canUpdateLeads}
                        entityId={detail.id}
                        entityType={ENTITY_TYPES.Lead}
                        externalLocationId={detail.externalLocationId}
                        externalBillToId={detail.billToContact.externalLinkId}
                        leadStatus={leadStatus}
                        requirementsAndCounts={detail.closingRequirements}
                        salesFunnelId={detail.salesFunnelId}
                        servicesCount={detail.servicesCount}
                        referralDiscountId={
                          detail.referralSummary?.referralDiscountId
                        }
                        hasValidLocationAddress={detail.hasValidLocationAddress}
                        scheduleSettings={detail.scheduleSettings}
                      />
                    </Stack>
                  </Paper>
                </Stack>
                <Stack m={2} mt={0}>
                  <Paper elevation={0} variant="outlined">
                    <Stack>
                      <Box p={2}>
                        <Typography
                          fontSize="20px"
                          color={theme.palette.text.primary}
                          fontFamily="Source Sans Pro Semibold"
                        >
                          Additional Information
                        </Typography>
                      </Box>
                      <EntityDetailsAdditionalInformation
                        canUpdate={canUpdateLeads}
                        entityId={detail.id}
                        entityType={ENTITY_TYPES.Lead}
                        detail={detail}
                        leadStatus={leadStatus}
                        salesTeamId={detail.salesTeamId.toString()}
                        entityMatched={leadMatched}
                      />
                    </Stack>
                  </Paper>
                </Stack>

                <Stack m={2} mt={0}>
                  <Paper elevation={0} variant="outlined">
                    <Contacts
                      canUpdateEntity={canUpdateLeads}
                      detail={detail}
                      entityId={detail.id}
                      entityType={ENTITY_TYPES.Lead}
                      addContactOpen={addContactOpen}
                      onCloseAddContact={onCloseAddContact}
                      onOpenAddContact={onOpenAddContact}
                    />
                  </Paper>
                </Stack>

                <Stack m={2} mt={0}>
                  <Paper elevation={0} variant="outlined">
                    <Notes
                      entityType={ENTITY_TYPES.Lead}
                      detail={detail}
                      canUpdate={canUpdateLeads}
                    />
                  </Paper>
                </Stack>

                {!!detail && (
                  <Stack m={2} mt={0}>
                    <Paper elevation={0} variant="outlined">
                      <ActivityTimelineCard
                        entityId={detail.id}
                        entityType={ENTITY_TYPES.Lead}
                        servicesCount={detail.closingRequirements.servicesCount}
                        agreementsCount={
                          detail.closingRequirements.agreementsCount
                        }
                        paymentsCount={detail.closingRequirements.paymentsCount}
                        appointments={detail.closingRequirements.appointments}
                        salesFunnelStageId={detail.salesFunnelStageId}
                        assigneeId={detail.assigneeId}
                      />
                    </Paper>
                  </Stack>
                )}
              </EntityDetailsContentWrapperStyled>
              {!!detail && !isMobile && (
                <Stack mt="20px!important" mr={2} spacing={1.5}>
                  <Content
                    entityId={detail.id}
                    entityType={ENTITY_TYPES.Lead}
                    salesTeamId={detail.salesTeamId}
                    ownerId={detail.assigneeId}
                  />

                  <Hidden mdDown>
                    <MostRecentKnockStatus knockStatus={recentKnockStatus} />
                  </Hidden>

                  {measureAIEnabled && (
                    <Measure
                      entityId={detail.id}
                      entityType={ENTITY_TYPES.Lead}
                      variant="detail"
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </LocationContextProvider>
        </SchedulingWizardContextProvider>
      </CWQDialogContextProvider>
    </LeadCompanyRequiredFieldsProvider>
  )
}
